import { type ReactNode, Suspense, useEffect, useState } from "react";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ReactQueryProductionDevtools } from "./ReactQueryProductionDevtools";
import {
  config,
  DISABLE_REACT_QUERY_DEVTOOLS,
  HMAC_SECRET,
} from "../../config";
import { playwrightEnv } from "../../helpers/testUtils/playwrightEnv";
import { logError } from "../../helpers/errors";
import { createConnectTransport } from "@connectrpc/connect-web";
import { TransportProvider } from "@connectrpc/connect-query";
import type { Interceptor } from "@connectrpc/connect";
import { buildHmacHeaders } from "../../helpers/hmacAuth";

const hmacInterceptor: Interceptor = next => async req => {
  const hmac = await buildHmacHeaders("POST", req.url, HMAC_SECRET);
  if (hmac) {
    req.header.set("X-Date", hmac.xDate);
    req.header.set("Authorization", hmac.authorization);
  }
  return next(req);
};

const finalTransport = createConnectTransport({
  baseUrl: config.CONNECT_BASE_URL,
  interceptors: [hmacInterceptor],
});

/** Wrapper for React Query */
export const QueryProvider = ({ children }: { children?: ReactNode }) => {
  // * State
  const [showProductionDevtools, setShowProductionDevtools] = useState(false);

  // * Query Client
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.errorMessage) {
          logError(String(query.meta.errorMessage), error);
        }
      },
    }),
  });

  // * Effects

  /**
   * calling `window.toggleDevtools()` will download the devtools bundle and show them.
   * @see https://tanstack.com/query/v5/docs/framework/react/devtools
   **/
  useEffect(() => {
    // this is disabled to allow us to add a function to window.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).toggleDevtools = () =>
      setShowProductionDevtools(old => !old);
  }, []);

  const disableReactQueryDevtools =
    // disabled for playwright
    playwrightEnv.DISABLE_REACT_QUERY_DEVTOOLS ||
    // env variable must be not be enabled
    DISABLE_REACT_QUERY_DEVTOOLS;

  return (
    <TransportProvider transport={finalTransport}>
      <QueryClientProvider client={queryClient}>
        {children}
        {!disableReactQueryDevtools && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
        {showProductionDevtools && (
          <Suspense fallback={null}>
            <ReactQueryProductionDevtools />
          </Suspense>
        )}
      </QueryClientProvider>
    </TransportProvider>
  );
};
